import { useState } from 'react';

import { Trans } from '@lingui/react/macro';

import KeyboardIcon from '../../../../common-components/elements/icon/svgs/Keyboard';
import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import {
  PracticeFeatureFlagKey,
  useFeatureFlag,
  useIsPatientViewEnabled,
} from '../../../../utils/user';

import * as Styled from './styles';

export const Shortcuts = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isPatientViewEnabled = useIsPatientViewEnabled();

  const isToothPartsEnabled = !!useFeatureFlag(
    PracticeFeatureFlagKey.TOOTH_PARTS_SEGMENTATIONS_ENABLED,
  )?.value;

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const isOpen = Boolean(anchorEl);

  return (
    <Styled.Shortcuts>
      <div
        aria-owns={isOpen ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      >
        <KeyboardIcon />
      </div>
      <Styled.Pop
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
          transform: 'translateY(-2em)',
        }}
        open={isOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <div className="shortcuts-popover">
          <VideaTypography variant="bodyExtrasmall" className="title">
            <Trans>Keyboard Shortcuts</Trans>
          </VideaTypography>
          <table>
            <tbody>
              <tr>
                <td>
                  <span className="key">A</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Show/hide all Annotations</Trans>
                  </VideaTypography>
                </td>
              </tr>
              {isPatientViewEnabled && (
                <tr>
                  <td>
                    <span className="key">V</span>
                  </td>
                  <td>
                    <VideaTypography variant="bodyExtrasmall">
                      <Trans>Turn on/off Patient View</Trans>
                    </VideaTypography>
                  </td>
                </tr>
              )}
              {isToothPartsEnabled && (
                <tr>
                  <td>
                    <span className="key">T</span>
                  </td>
                  <td>
                    <VideaTypography variant="bodyExtrasmall">
                      <Trans>Turn on/off Tooth Parts Segmentation</Trans>
                    </VideaTypography>
                  </td>
                </tr>
              )}

              <tr>
                <td>
                  <span className="key">R</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Show/hide Restorative Annotations</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">P</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Show/hide Perio Annotations</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">E</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Show/hide Endo Annotations</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">←</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Previous Radiograph</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">→</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Next Radiograph</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">.</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Rotate</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">H</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>HQ</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">M</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Magnify</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">I</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Invert</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">1</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Window Level (brightness) 1</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">2</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Window Level (brightness) 2</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">3</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Window Level (brightness) 3</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">4</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Window Level (brightness) 4</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">5</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Window Level (brightness) 5</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">6</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Window Level (brightness) 6</Trans>
                  </VideaTypography>
                </td>
              </tr>
              <tr>
                <td>
                  <span className="key">D</span>
                </td>
                <td>
                  <VideaTypography variant="bodyExtrasmall">
                    <Trans>Reset Toolbar Settings</Trans>
                  </VideaTypography>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Styled.Pop>
    </Styled.Shortcuts>
  );
};
